import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type JSX, type RefObject } from 'react'
import { api, changesOf, roleNames, stampWithZone, type AuditEvent, type AuditQuery } from '../lib/api'
import { AuditFilters } from '../components/AuditFilters'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { Toggletip } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { auditLabel } from '../lib/auditLabels'
import { createAuditScreen, type AuditScreen } from '../lib/auditScreen'
import { TRAIL_PAGE, createTrail, namesOneRecord, trailFor, type Trail, type TrailState } from '../lib/trail'
import '../styles/sweep.css'
import '../styles/lists.css'

// The reader for the evidence chain.
//
// WHY A SCREEN AND NOT A VERIFY BUTTON
//
// Around forty acts append to a per-tenant hash chain the database refuses to
// rewrite, and the Evidence screen rehashes the whole of it. That answers
// "has anything been altered", which is not the question an auditor arrives
// with. Theirs is who changed this vendor's tier, and when, and what did the
// record say before. Until this screen the only answer was a psql session.
//
// WHY THE ROWS ARE NOT SORTABLE
//
// Every other register in this console hands DataTable a sortValue per column
// and lets the reader reorder. Here the order is the evidence: the chain hashes
// its rows in sequence, and a page is a window on that sequence rather than a
// set of rows that happens to have arrived together. Sorting the two pages
// somebody has read so far by actor would produce an ordering that is true of
// nothing, and would reorder itself the moment a third page arrived. The order
// the API returns is the order the chain holds, and it is the only one on
// offer.
//
// WHY THE ACTS READ AS WORDS
//
// The chain records a code per act, and until 21 September 2026 the screen
// showed it ("campaign.approved"). The product officer's review (observation
// 3.19) asked for words, grouped the way the product is: every act has a label
// and a group in lib/auditLabels.ts, and a test fails on an act without one.
// The code is still one click away, in the row's record, for the reader who is
// matching the chain against another system.
//
// WHY THE PAYLOAD IS PRINTED AND NOT SUMMARISED
//
// The payload is whatever the act wrote at the time. Reshaping it here would
// report this year's opinion of what mattered about an act from eighteen months
// ago, which is the confusion an audit trail exists to prevent. It is printed
// as it arrived, formatted and nothing else, behind a disclosure so a hundred
// rows are still a list.

// One filter, read from the start each time it changes, then extended by the
// cursor. The query is compared by its serialised form rather than by identity,
// because a caller building { entityType: 'vendor', entityId: id } inline hands
// a new object on every render and would otherwise refetch forever.
//
// The reading itself, and what guards it, is lib/trail.ts, where a test can
// drive it. This connects it to a component, and hands over only rows read for
// the filter the component is on now: the render that follows a change of
// filter comes before the read for it has started, and what the reading holds
// then is the last filter's answer.
//
// given is a reading handed in by a test, already holding what the test put
// in it. A screen makes its own.
function useTrail(query: AuditQuery, given?: Trail): TrailState & { readFurther: () => void } {
  const key = JSON.stringify(query)
  const [trail] = useState(() => given ?? createTrail((q) => api.audit(q)))
  const state = useSyncExternalStore(trail.subscribe, trail.state, trail.state)

  useEffect(() => trail.start(key), [trail, key])
  useEffect(() => trail.stop, [trail])

  return { ...trailFor(state, key), readFurther: trail.further }
}

// In what capacity, and from where.
//
// Read out of the payload rather than off a column, because the chain hashes
// the payload and does not hash the columns: a role in a column would be
// recorded and unprotected. See packages/core/evidence/actor.go. Events written
// before this existed carry nothing here and say so by falling back to the
// actor type, which is what the column showed before.
function actorOf(e: AuditEvent): { roles: string[]; ip: string } {
  const p = e.payload as { actor?: { roles?: unknown; ip?: unknown } } | null
  const a = p && typeof p === 'object' ? p.actor : undefined
  const roles = Array.isArray(a?.roles) ? a.roles.filter((r): r is string => typeof r === 'string') : []
  return { roles, ip: typeof a?.ip === 'string' ? a.ip : '' }
}

function words(v: string): string {
  return v.replace(/_/g, ' ')
}

// Why the act was done, where the act says. Reassess now, a dismissed
// trigger, a pause and a treatment decision each carry one, and it is the line
// a reader scanning the trail most wants without opening the record.
function reasonOf(e: AuditEvent): string {
  const p = e.payload as { reason?: unknown } | null
  return p && typeof p === 'object' && typeof p.reason === 'string' ? p.reason.trim() : ''
}

// The columns, with no sortValue anywhere. See the header.
//
// withEntity is off on a record's own History tab, where the column would print
// the same type and the same identifier on every row: the record is what the
// reader is already looking at, and repeating its uuid forty times is how a
// column stops being read.
function columns(withEntity: boolean): Column<AuditEvent>[] {
  const out: Column<AuditEvent>[] = [
    {
      key: 'when',
      header: 'When',
      render: (e) => (
        <>
          <span className="cell-primary mono audit-when">{stampWithZone(e.occurredAt)}</span>
          <span className="cell-sub">sequence {e.sequence}</span>
        </>
      ),
    },
    {
      key: 'action',
      header: 'What was done',
      render: (e) => {
        const why = reasonOf(e)
        return (
          <>
            <span className="cell-primary">{auditLabel(e.action)}</span>
            {why && <span className="cell-sub audit-reason">Reason: {why}</span>}
          </>
        )
      },
    },
    {
      key: 'actor',
      header: 'By whom',
      render: (e) => {
        const a = actorOf(e)
        return (
          <>
            <span className="mono">{e.actorId || 'not recorded'}</span>
            <span className="cell-sub">
              {a.roles.length > 0 ? roleNames(a.roles).join(', ') : e.actorType.toLowerCase()}
            </span>
            {a.ip && <span className="cell-sub mono">from {a.ip}</span>}
          </>
        )
      },
    },
  ]

  if (withEntity) {
    out.push({
      key: 'entity',
      header: 'To what',
      render: (e) => (
        <>
          {/* A vendor id is a record somebody can open. Everything else is an
              id and stays one: an assessment or a finding is reached through
              its own vendor, and a link that lands on a screen the console does
              not have is worse than a printed identifier. */}
          {e.entityType === 'vendor' ? (
            <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(e.entityId)}>
              {words(e.entityType)}
            </a>
          ) : (
            <span className="cell-primary">{words(e.entityType)}</span>
          )}
          <span className="cell-sub mono">{e.entityId}</span>
        </>
      ),
    })
  }

  out.push({
    key: 'changed',
    header: 'What changed',
    render: (e) => <WhatChanged event={e} />,
  })

  out.push({
    key: 'record',
    header: 'What was written',
    render: (e) => <EventRecord event={e} />,
  })

  return out
}

const TRAIL_COLUMNS = columns(true)
const RECORD_COLUMNS = columns(false)

// The old value and the new one, on the row, without opening anything.
//
// Requirement 19 asks for both, and until the acts started recording them the
// only answer was the payload, where each act spelled its before differently
// or did not carry one at all. Putting this in a column rather than inside the
// expander is the point: an auditor scanning a fortnight for the moment a tier
// moved should see it while scrolling, not after opening forty rows.
//
// An act that created something moved nothing, and says so rather than showing
// an empty cell, because a blank column reads as a gap in the record.
function WhatChanged({ event }: { event: AuditEvent }): JSX.Element {
  const changes = changesOf(event)
  if (changes.length === 0) return <span className="muted">Nothing was replaced</span>
  return (
    <ul className="audit-changes">
      {changes.map((c) => (
        <li key={c.field}>
          <span className="cell-primary">{c.field}</span>
          <span className="cell-sub">
            {c.from === '' ? 'not set' : c.from} to {c.to === '' ? 'not set' : c.to}
          </span>
        </li>
      ))}
    </ul>
  )
}

function EventRecord({ event }: { event: AuditEvent }): JSX.Element {
  // A closed row builds nothing. Fifty rows of formatted JSON on every render
  // of the table is fifty pieces of work for the one row somebody opened, and
  // a scored assessment's payload is not small.
  const [open, setOpen] = useState(false)
  return (
    <details className="audit-record" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>
        <span className="audit-record-cue">Payload and hash</span>
        <span className="mono audit-hash-short">{event.eventHash.slice(0, 12)}</span>
      </summary>
      {open && (
        <>
          <pre className="audit-json">{JSON.stringify(event.payload, null, 2)}</pre>
          <dl className="audit-hashes">
            <dt>Act</dt>
            <dd className="mono">{event.action}</dd>
            <dt>Event</dt>
            <dd className="mono">{event.eventId}</dd>
            <dt>This hash</dt>
            <dd className="mono">{event.eventHash}</dd>
            <dt>Hash before</dt>
            <dd className="mono">{event.previousHash || 'nothing: this is the first act in the chain'}</dd>
            <dt>Correlation</dt>
            <dd className="mono">{event.correlationId || 'not recorded'}</dd>
          </dl>
        </>
      )}
    </details>
  )
}

// What the end of a page means, said rather than left as a list that stops.
// A reader who cannot tell "there is no more" from "it stopped loading" checks
// the trail somewhere else.
function TrailFoot({
  events,
  nextBefore,
  loading,
  onRead,
}: {
  events: AuditEvent[]
  nextBefore: number
  loading: boolean
  onRead: () => void
}): JSX.Element | null {
  const oldest = events[events.length - 1]
  if (!oldest) return null
  if (nextBefore === 0) {
    return (
      <p className="audit-foot audit-foot-said sw-row" role="status">
        <strong>This is the beginning of the trail</strong>
        <span>
          Nothing earlier matches, the oldest is sequence {oldest.sequence} at{' '}
          <span className="mono">{stampWithZone(oldest.occurredAt)}</span>
        </span>
      </p>
    )
  }
  return (
    <div className="audit-foot audit-foot-read">
      <button type="button" onClick={onRead} disabled={loading}>
        {loading ? 'Reading' : 'Read further back'}
      </button>
      <span className="hint">
        The next {TRAIL_PAGE} acts below sequence {nextBefore}
      </span>
    </div>
  )
}

// How long the typed boxes wait before the trail is read with them. Long
// enough that an address is not read a letter at a time, short enough that the
// trail has answered by the time the reader looks down at it.
const TYPING_PAUSE_MS = 350

// screen is the screen's state and what changes it, with no component in it
// (lib/auditScreen.ts). A test hands one in, in whatever state it has put it;
// the console makes its own.
export function Audit({ screen: given }: { screen?: AuditScreen } = {}): JSX.Element {
  const [screen] = useState(
    () =>
      given ??
      createAuditScreen({ read: (q) => api.audit(q), acts: () => api.auditActions().then((r) => r.actions) }, TYPING_PAUSE_MS),
  )
  const { choices, applied, trail, actions, actionsError } = useSyncExternalStore(screen.subscribe, screen.state, screen.state)

  useEffect(() => {
    screen.open()
    return screen.close
  }, [screen])

  // How tall the rows last drawn were, which the card keeps while the next
  // answer is read. See TrailCard.
  const body = useRef<HTMLDivElement>(null)
  const [held, setHeld] = useState(0)
  useLayoutEffect(() => {
    const drawn = body.current?.offsetHeight ?? 0
    if (drawn > 0) setHeld(drawn)
  })

  // What the trail has been seen to hold, offered as suggestions under the
  // actor and kind boxes. There is no route that lists either vocabulary, and
  // inventing a fixed list here would offer a filter for a type this tenant
  // has never recorded and hide one it has. The API matches an actor exactly,
  // so one picked from the rows is one that will be found.
  const seenTypes = useMemo(() => [...new Set(trail.events.map((e) => e.entityType))].sort(), [trail.events])
  const seenActors = useMemo(
    () => [...new Set(trail.events.map((e) => e.actorId).filter(Boolean))].sort(),
    [trail.events],
  )

  return (
    <>
      <div className="page-head">
        <div>
          {/* How the API read this filter, in its own words, behind the (i):
              the chips say what was asked and the count under the rows says
              how much of the trail that left out. A filter on one record is
              read differently, and its note is on the page (TrailCard). */}
          <h1 className="sw-title">
            Audit trail
            {!namesOneRecord(trail.key) && <ReasonTip label="About this trail" text={trail.head?.note} />}
          </h1>
        </div>
      </div>

      <AuditFilters
        choices={choices}
        applied={applied}
        onChoose={screen.choose}
        onType={screen.type}
        actions={actions}
        actionsError={actionsError}
        onReadActions={screen.readActs}
        seenActors={seenActors}
        seenTypes={seenTypes}
      />

      {trail.error != null && <ErrorBox error={trail.error} what="The audit trail" />}

      <TrailCard trail={trail} held={held} body={body} onReadFurther={screen.further} />
    </>
  )
}

// The card the trail is drawn in.
//
// WHILE A CHANGED FILTER IS BEING READ, IT HOLDS NO ROWS
//
// For an afternoon this card kept the last answer on the screen, dimmed, until
// the next one landed, so the page would not shorten under a reader between
// one tick and the next. The first review (11 October 2026) saw what that
// costs: the old rows and the old "22 acts" sit under the new chip and read as
// the answer to it. On this screen that is a person shown acts their filter
// excludes. So the rows and their count go the moment the filter changes, as
// they always did, and what is kept is only the height they stood at, so the
// page still does not jump.
//
// The height alone was not enough. Two and a half thousand pixels of card with
// one line at its top is, to somebody 900 px down it, an empty result. The
// held space is drawn as space that is waiting (audit-held), and the line that
// says a read is out stays in the window as it is scrolled.
//
// A read that was refused leaves nothing to draw, and an empty card under the
// refusal would read as a trail with nothing in it, so there is no card then.
export function TrailCard({
  trail,
  held = 0,
  body,
  onReadFurther,
}: {
  trail: TrailState
  held?: number
  body?: RefObject<HTMLDivElement | null>
  onReadFurther: () => void
}): JSX.Element | null {
  const head = trail.head
  // The filter names one record. The API's note then says what the rows are
  // (each act as it was written, not the record as it stands), which changes
  // how they are read, so it is printed above them. Read off the rows' own
  // filter, so the note cannot be for one filter and the rows for another.
  const onRecord = namesOneRecord(trail.key)
  if (head === null && !trail.loading) return null
  return (
    <section
      // Not clipped while it waits: a clipped card is its own scroll box, and
      // the line that says a read is out would stick to it instead of to the
      // window.
      className={'panel panel-flush' + (head === null ? ' audit-trail-waiting' : '')}
      aria-labelledby="audit-trail-heading"
      aria-busy={trail.loading}
    >
      <h2 id="audit-trail-heading" className="vh">
        The trail
      </h2>

      {head === null ? (
        <div className={held > 0 ? 'audit-held' : undefined} style={held > 0 ? { minHeight: held } : undefined}>
          <div className="audit-reading">
            <Loading what="the audit trail" />
          </div>
        </div>
      ) : (
        <div ref={body}>
          {onRecord && head.note && <p className="audit-note-open">{head.note}</p>}
          <DataTable
            caption="Acts recorded against this tenant, newest first, in the order the chain holds them."
            columns={TRAIL_COLUMNS}
            rows={trail.events}
            rowKey={(e) => e.eventId}
            empty={{
              title: 'No act in this chain matches these filters',
              detail:
                head.total > 0
                  ? 'The chain holds ' + head.total + ' acts, widen or clear the filters above to see them'
                  : 'Nothing recorded yet, which is what an untouched tenant looks like',
            }}
            // Read against the whole chain, always. A filtered answer
            // reporting only its own row count hides how much of the trail
            // it left out, and that omission is what makes a confident
            // screen wrong.
            footer={<ListCount shown={trail.events.length} total={head.total} noun="acts" />}
          />
          <TrailFoot events={trail.events} nextBefore={trail.nextBefore} loading={trail.loading} onRead={onReadFurther} />
        </div>
      )}
    </section>
  )
}

// The same trail, narrowed to one vendor, for the record's own History tab.
// The question "who changed this and when" is asked while looking at the thing
// that changed, and sending somebody to a separate screen with a filter to fill
// in is how that question goes unanswered.
export function VendorHistory({ vendorId, trail: given }: { vendorId: string; trail?: Trail }): JSX.Element {
  const filter = useMemo<AuditQuery>(() => ({ entityType: 'vendor', entityId: vendorId }), [vendorId])
  const trail = useTrail(filter, given)

  return (
    <section className="panel panel-flush" aria-labelledby="vendor-history-heading">
      <div className="panel-head">
        <h2 id="vendor-history-heading" style={{ margin: 0 }}>
          History
        </h2>
      </div>

      {/* Where the rest of the vendor's acts are is the line; the API's note on
          how this history is read, word for word, and why those acts are not
          here sit behind one (i). Why there is no total is said beside the
          count it explains. */}
      <p className="field-note audit-note sw-row">
        <span>
          Assessments, findings and incidents keep their own acts in <a href="#/audit">the audit trail</a>
        </span>
        <ReasonTip
          label="About this history"
          text={
            (trail.head?.note ?? 'Everything recorded against this record, newest first.') +
            " Acts against this vendor's assessments, findings and incidents carry their own identifiers and are not here."
          }
        />
      </p>

      {trail.error != null && <ErrorBox error={trail.error} what="This record's history" />}
      {trail.head === null && trail.loading && <Loading what="this record's history" />}

      {trail.head && (
        <>
          <DataTable
            caption="Acts recorded against this vendor, newest first, in the order the chain holds them."
            columns={RECORD_COLUMNS}
            rows={trail.events}
            rowKey={(e) => e.eventId}
            // Not "of N in the tenant's chain". That total counts every act in
            // the tenant and the figure beside it counts one page of this
            // record's, so the pair read as a fraction of nothing. How many
            // acts stand against this record is not in the response, so the
            // line says what has been read and whether the chain holds more.
            footer={
              <ListCount shown={trail.events.length} noun="acts">
                {trail.nextBefore > 0 ? (
                  <>
                    {' '}read, and the chain holds more{' '}
                    <Toggletip label="Why there is no total">
                      Read further for the rest; the chain reports no total for a single record.
                    </Toggletip>
                  </>
                ) : (
                  ', all of them'
                )}
              </ListCount>
            }
            empty={{
              title: 'Nothing has been recorded against this record',
              detail: (
                <>
                  Anything done to it from here on appends an act{' '}
                  <Toggletip label="Why a vendor can have none">
                    A vendor raised before the chain existed carries no acts.
                  </Toggletip>
                </>
              ),
            }}
          />
          <TrailFoot
            events={trail.events}
            nextBefore={trail.nextBefore}
            loading={trail.loading}
            onRead={trail.readFurther}
          />
        </>
      )}
    </section>
  )
}
